import { type UseQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Run, RunStep } from '@ankor-io/common/lang/backend/pipeline/types'
import type { ListOrganizationsRequest, OrganizationLighthouse } from '@ankor-io/common/types/account'
import type { AWXCardResponse, AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import { type CardPayload, type ReconciledCardPayload } from '@ankor-io/common/types/cards'
import type { QuickBooksPipelineData, QuickBooksSyncJobPayload } from '@ankor-io/common/types/integrations/quickbooks'
import type { IntegrationPayload } from '@ankor-io/common/types/integrations/types'
import {
  type XeroConnectionPayload,
  type XeroConnectionSyncRequest,
  type XeroIntegrationPipelinePayload,
  type XeroIntegrationPipelineStageLogPayload,
  type XeroIntegrationPipelineTransactionPayload,
  type XeroOrganizationOverview,
  type XeroSyncJobPayload,
  type XeroSyncJobRunDetailPayload,
  type XeroSyncJobRunPayload,
  type XeroSyncJobRunStepPayload,
} from '@ankor-io/common/types/integrations/xero/types'
import type { LighthouseGlobalAccountPayload } from '@ankor-io/common/types/lighthouse'
import type { VesselAndConnectedAccounts, VesselConnectedAccountAccess } from '@ankor-io/common/types/pay'
import type { FeeSource, OrganizationFeesPayload, UpdateOrganizationPayFeesBody } from '@ankor-io/common/types/pay/fee'
import type {
  GeneratedReportStatus,
  ReportGenerationJobPayload,
  ReportGenerationPipelineData,
} from '@ankor-io/common/types/reports/generated-report'
import type {
  FeatureToggleName,
  FeatureToggleValue,
  OrganizationFeatureTogglesPayload,
  UpdateOrganizationTogglesBody,
} from '@ankor-io/common/types/system'
import type { ReconciledTransferPayload } from '@ankor-io/common/types/transaction'

import { lighthouseService } from '@/api/services'
import { useUser } from '@/hooks/useUser'

/**
 * Query key factory for organization feature.
 * Provides centralized query key management for all organization-related queries.
 *
 * @example
 * ```ts
 * // Invalidate all organization queries
 * queryClient.invalidateQueries({ queryKey: organizationKeys.all })
 *
 * // Invalidate a specific organization's data
 * queryClient.invalidateQueries({ queryKey: organizationKeys.detail('org-123') })
 * ```
 */
export const organizationKeys = {
  all: ['organizations'],
  lists: () => [...organizationKeys.all, 'list'],
  list: (filters: ListOrganizationsRequest) => [...organizationKeys.lists(), filters],
  infinite: (filters: Omit<ListOrganizationsRequest, 'skip' | 'take'>, pageSize: number) => [
    ...organizationKeys.lists(),
    'infinite',
    filters,
    pageSize,
  ],
  details: () => [...organizationKeys.all, 'detail'],
  detail: (id?: string) => [...organizationKeys.details(), id],
  toggles: (id?: string) => [...organizationKeys.detail(id), 'toggles'],
  fees: (id?: string) => [...organizationKeys.detail(id), 'fees'],
  integrationsList: (id?: string) => [...organizationKeys.detail(id), 'integrations'],
  quickbooks: (id?: string) => [...organizationKeys.detail(id), 'quickbooks'],
  quickbooksSyncJobs: (id?: string) => [...organizationKeys.quickbooks(id), 'jobs'],
  quickbooksSyncJobRuns: (id?: string, jobId?: string) => [...organizationKeys.quickbooksSyncJobs(id), jobId, 'runs'],
  quickbooksSyncJobRun: (id?: string, jobId?: string, runId?: string) => [
    ...organizationKeys.quickbooksSyncJobRuns(id, jobId),
    runId,
  ],
  quickbooksSyncJobRunSteps: (id?: string, jobId?: string, runId?: string, stepType?: string) => [
    ...organizationKeys.quickbooksSyncJobRun(id, jobId, runId),
    'steps',
    stepType,
  ],
  xero: (id?: string) => [...organizationKeys.detail(id), 'xero'],
  xeroSyncJobs: (id?: string, connectionId?: string) => [
    ...organizationKeys.xero(id),
    'connections',
    connectionId,
    'jobs',
  ],
  xeroSyncJobRuns: (id?: string, connectionId?: string, jobId?: string) => [
    ...organizationKeys.xeroSyncJobs(id, connectionId),
    jobId,
    'runs',
  ],
  xeroSyncJobRun: (id?: string, connectionId?: string, jobId?: string, runId?: string) => [
    ...organizationKeys.xeroSyncJobRuns(id, connectionId, jobId),
    runId,
  ],
  xeroSyncJobRunSteps: (id?: string, connectionId?: string, jobId?: string, runId?: string, stepType?: string) => [
    ...organizationKeys.xeroSyncJobRun(id, connectionId, jobId, runId),
    'steps',
    stepType,
  ],
  xeroPipeline: (id?: string, connectionId?: string, jobId?: string) => [
    ...organizationKeys.xeroSyncJobs(id, connectionId),
    jobId,
    'pipeline',
  ],
  xeroPipelineTransactions: (id?: string, connectionId?: string, jobId?: string) => [
    ...organizationKeys.xeroPipeline(id, connectionId, jobId),
    'transactions',
  ],
  xeroPipelineTransactionLogs: (id?: string, connectionId?: string, jobId?: string, transactionId?: string) => [
    ...organizationKeys.xeroPipeline(id, connectionId, jobId),
    'transactions',
    transactionId,
    'logs',
  ],
  reportGenJobs: (id?: string) => [...organizationKeys.detail(id), 'reportGen', 'jobs'],
  reportGenJobRuns: (id?: string, jobId?: string) => [...organizationKeys.reportGenJobs(id), jobId, 'runs'],
  reportGenJobRun: (id?: string, jobId?: string, runId?: string) => [
    ...organizationKeys.reportGenJobRuns(id, jobId),
    runId,
  ],
  reportGenRunSteps: (id?: string, jobId?: string, runId?: string, stepType?: string) => [
    ...organizationKeys.reportGenJobRun(id, jobId, runId),
    'steps',
    stepType,
  ],
}

/**
 * TanStack Infinite Query hook for fetching organizations with infinite scroll.
 *
 * @param request - Base request parameters (filters only, skip will be managed by pageParam)
 * @param pageSize - Number of organizations to fetch per page (default: 20)
 * @returns Infinite query result with pages of organizations, loading state, and fetchNextPage function
 *
 * @example
 * ```ts
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage
 * } = useInfiniteOrganizationsQuery({
 *   visibility: ['visible']
 * }, 20)
 *
 * // Access all organizations across pages
 * const allOrgs = data?.pages.flat() ?? []
 * ```
 */
export function useInfiniteOrganizationsQuery(
  request: Omit<ListOrganizationsRequest, 'skip' | 'take'> = {},
  pageSize = 20,
) {
  return useInfiniteQuery<OrganizationLighthouse[], Error>({
    queryKey: organizationKeys.infinite(request, pageSize),
    queryFn: ({ pageParam = 0 }) =>
      lighthouseService.listOrganizations({
        ...request,
        skip: pageParam as number,
        take: pageSize,
      }),
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer items than pageSize, we've reached the end
      if (lastPage.length < pageSize) {
        return undefined
      }
      // Return the skip value for the next page
      return allPages.length * pageSize
    },
    initialPageParam: 0,
  })
}

/**
 * TanStack Query hook for fetching a single organization by ID.
 *
 * @param id - Optional organization ID. The query is disabled when it is missing.
 * @param options - Optional query configuration
 * @returns Query result with organization data, loading state, and error
 *
 * @example
 * ```ts
 * const { data, error, isPending } = useOrganizationDetailsQuery('org-123')
 * ```
 */
export function useOrganizationDetailsQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery<OrganizationLighthouse, Error>({
    queryKey: organizationKeys.detail(id),
    queryFn: () => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.getOrganization(id)
    },
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * TanStack Query hook for fetching feature toggles for a specific organization.
 *
 * @param id - Optional organization ID. The query is disabled when it is missing.
 * @param options - Optional query configuration
 * @returns Query result with feature toggles data (with globalDefault and orgValue), loading state, and error
 *
 * @example
 * ```ts
 * const { data, error, isPending } = useOrganizationFeatureTogglesQuery('org-123')
 * console.log(data.toggles.TEST_FEATURE_TOGGLE.globalDefault) // 'enabled' or 'disabled'
 * console.log(data.toggles.TEST_FEATURE_TOGGLE.orgValue) // 'enabled', 'disabled', or null
 * ```
 */
export function useOrganizationFeatureTogglesQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery<OrganizationFeatureTogglesPayload, Error>({
    queryKey: organizationKeys.toggles(id),
    queryFn: () => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.getOrganizationToggles(id)
    },
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * TanStack Mutation hook for updating feature toggles for an organization.
 * Uses optimistic updates for instant UI feedback, with automatic rollback on error.
 * Automatically invalidates the toggles query on success to ensure consistency.
 *
 * @param id - Optional organization ID. The mutation throws if invoked without a defined ID.
 * @returns Mutation result with mutate function, loading state, and error
 *
 * @example
 * ```ts
 * const { mutate: updateToggles, isPending } = useUpdateOrganizationFeatureTogglesMutation('org-123')
 *
 * updateToggles({ toggles: [{ name: 'TEST_FEATURE_TOGGLE', value: 'enabled' }] }, {
 *   onSuccess: () => {
 *     console.log('Toggles updated successfully')
 *   }
 * })
 * ```
 */
export function useUpdateOrganizationFeatureTogglesMutation(id?: string) {
  const queryClient = useQueryClient()
  return useMutation<
    OrganizationFeatureTogglesPayload,
    Error,
    UpdateOrganizationTogglesBody,
    { previousData: OrganizationFeatureTogglesPayload | undefined } | undefined
  >({
    mutationFn: (body: UpdateOrganizationTogglesBody) => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.updateOrganizationToggles(id, body)
    },
    onMutate: async (variables) => {
      if (!id) return undefined

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: organizationKeys.toggles(id) })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<OrganizationFeatureTogglesPayload>(organizationKeys.toggles(id))

      // Optimistically update the cache
      if (previousData) {
        const optimisticData: OrganizationFeatureTogglesPayload = {
          ...previousData,
          toggles: { ...previousData.toggles },
        }

        // Update each toggle in the mutation
        for (const toggle of variables.toggles) {
          const toggleName = toggle.name as FeatureToggleName
          const newValue = toggle.value as FeatureToggleValue
          const currentToggle = optimisticData.toggles[toggleName]

          if (currentToggle) {
            // If new value matches globalDefault, set orgValue to null (follows global)
            // Otherwise, set orgValue to the new value (override)
            optimisticData.toggles[toggleName] = {
              ...currentToggle,
              orgValue: newValue === currentToggle.globalDefault ? null : newValue,
            }
          }
        }

        queryClient.setQueryData(organizationKeys.toggles(id), optimisticData)
      }

      // Return context with previous data for rollback
      return { previousData }
    },
    onError: (_error, _variables, context) => {
      // Rollback to previous data on error
      if (id && context?.previousData) {
        queryClient.setQueryData(organizationKeys.toggles(id), context.previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      if (id) {
        queryClient.invalidateQueries({ queryKey: organizationKeys.toggles(id) })
      }
    },
  })
}

/**
 * TanStack Mutation hook for deleting a specific feature toggle override for an organization.
 * Removes the organization-specific override, reverting the toggle to the global default value.
 * Uses optimistic updates for instant UI feedback, with automatic rollback on error.
 * Automatically invalidates the toggles query on success to ensure consistency.
 *
 * @param id - Optional organization ID. The mutation throws if invoked without a defined ID.
 * @returns Mutation result with mutate function, loading state, and error
 *
 * @example
 * ```ts
 * const { mutate: deleteToggle, isPending } = useDeleteOrganizationToggleMutation('org-123')
 *
 * deleteToggle('TEST_FEATURE_TOGGLE', {
 *   onSuccess: () => {
 *     console.log('Toggle override deleted successfully')
 *   }
 * })
 * ```
 */
export function useDeleteOrganizationToggleMutation(id?: string) {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string, { previousData: OrganizationFeatureTogglesPayload | undefined } | undefined>({
    mutationFn: (toggleName: string) => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.deleteOrganizationToggle(id, toggleName)
    },
    onMutate: async (toggleName) => {
      if (!id) return undefined

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: organizationKeys.toggles(id) })
      await queryClient.cancelQueries({ queryKey: organizationKeys.detail(id) })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<OrganizationFeatureTogglesPayload>(organizationKeys.toggles(id))

      // Optimistically update the cache
      if (previousData) {
        const optimisticData: OrganizationFeatureTogglesPayload = {
          ...previousData,
          toggles: { ...previousData.toggles },
        }

        // Set orgValue to null (revert to global default)
        const toggleNameTyped = toggleName as FeatureToggleName
        const currentToggle = optimisticData.toggles[toggleNameTyped]

        if (currentToggle) {
          optimisticData.toggles[toggleNameTyped] = {
            ...currentToggle,
            orgValue: null, // Revert to global default
          }

          queryClient.setQueryData(organizationKeys.toggles(id), optimisticData)
        }
      }

      // Return context with previous data for rollback
      return { previousData }
    },
    onError: (_error, _toggleName, context) => {
      // Rollback to previous data on error
      if (id && context?.previousData) {
        queryClient.setQueryData(organizationKeys.toggles(id), context.previousData)
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure consistency
      if (id) {
        queryClient.invalidateQueries({ queryKey: organizationKeys.toggles(id) })
        queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) })
      }
    },
  })
}

/**
 * TanStack Query hook for fetching fees for a specific organization.
 *
 * @param id - Optional organization ID. The query is disabled when it is missing.
 * @param options - Optional query configuration
 * @returns Query result with fees data, loading state, and error
 */
export function useOrganizationFeesQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery<OrganizationFeesPayload, Error>({
    queryKey: organizationKeys.fees(id),
    queryFn: () => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.getOrganizationFees(id)
    },
    enabled: options?.enabled !== false && !!id,
  })
}

/**
 * TanStack Mutation hook for updating fees for an organization.
 * Automatically invalidates the fees query on success to refetch fresh data.
 *
 * @param id - Optional organization ID. The mutation throws if invoked without a defined ID.
 * @returns Mutation result with mutate function, loading state, and error
 *
 * @example
 * ```ts
 * const { mutate: updateFees, isPending } = useUpdateOrganizationFeesMutation('org-123')
 *
 * updateFees({ fees: [{ source: 'CONVERSION', type: 'PERCENTAGE', amount: 0.9 }] }, {
 *   onSuccess: () => {
 *     console.log('Fees updated successfully')
 *   }
 * })
 * ```
 */
export function useUpdateOrganizationFeesMutation(id?: string) {
  const queryClient = useQueryClient()
  return useMutation<OrganizationFeesPayload, Error, UpdateOrganizationPayFeesBody>({
    mutationFn: (body: UpdateOrganizationPayFeesBody) => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.updateOrganizationFees(id, body)
    },
    onSuccess: () => {
      if (id) {
        queryClient.invalidateQueries({ queryKey: organizationKeys.fees(id) })
      }
    },
  })
}

/**
 * TanStack Mutation hook for batch deleting multiple fee overrides for an organization.
 * Deletes fees sequentially and invalidates the query once after all deletions complete.
 * This prevents race conditions from multiple parallel invalidations.
 *
 * @param id - Optional organization ID. The mutation throws if invoked without a defined ID.
 * @returns Mutation result with mutateAsync function for batch operations
 *
 * @example
 * ```ts
 * const { mutateAsync: deleteFeesBatch } = useBatchDeleteOrganizationFeesMutation('org-123')
 *
 * await deleteFeesBatch(['CONVERSION', 'TRANSFER'])
 * ```
 */
export function useBatchDeleteOrganizationFeesMutation(id?: string) {
  const queryClient = useQueryClient()
  return useMutation<void, Error, FeeSource[]>({
    mutationFn: async (feeSources: FeeSource[]) => {
      if (!id) {
        throw new Error('Organization ID is required')
      }
      // Delete fees sequentially to avoid race conditions
      for (const feeSource of feeSources) {
        await lighthouseService.deleteOrganizationFee(id, feeSource)
      }
    },
    onSuccess: () => {
      // Invalidate once after all deletions complete
      if (id) {
        queryClient.invalidateQueries({ queryKey: organizationKeys.fees(id) })
      }
    },
  })
}

export function useOrganizationIntegrationsQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery<IntegrationPayload[], Error>({
    queryKey: organizationKeys.integrationsList(id),
    queryFn: () => {
      if (!id) throw new Error('Organization ID is required')
      return lighthouseService.listOrganizationIntegrations(id)
    },
    enabled: options?.enabled !== false && !!id,
  })
}

export function useOrganizationXeroOverviewQuery(id?: string, options?: { enabled?: boolean }) {
  return useQuery<XeroOrganizationOverview | null, Error>({
    queryKey: [...organizationKeys.xero(id), 'overview'],
    queryFn: () => {
      if (!id) throw new Error('Organization ID is required')
      return lighthouseService.getOrganizationXeroOverview(id)
    },
    enabled: options?.enabled !== false && !!id,
  })
}

export function useXeroConnectionsQuery<TSelected = XeroConnectionPayload[]>(
  orgId?: string,
  options?: Omit<UseQueryOptions<XeroConnectionPayload[], Error, TSelected>, 'queryKey' | 'queryFn' | 'enabled'> & {
    enabled?: boolean
  },
) {
  const { enabled: enabledOption, ...queryOptions } = options ?? {}
  return useQuery<XeroConnectionPayload[], Error, TSelected>({
    queryKey: [...organizationKeys.xero(orgId), 'connections'],
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      return lighthouseService.listOrganizationXeroConnections(orgId)
    },
    enabled: enabledOption !== false && !!orgId,
    ...queryOptions,
  })
}

export function useXeroSyncJobsQuery(
  orgId?: string,
  connectionId?: string,
  options?: { enabled?: boolean; connectedAccountName?: string },
) {
  return useQuery<XeroSyncJobPayload[], Error>({
    queryKey: organizationKeys.xeroSyncJobs(orgId, connectionId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      return lighthouseService.listXeroSyncJobs(orgId, connectionId, {
        connectedAccountName: options?.connectedAccountName,
      })
    },
    enabled: options?.enabled !== false && !!orgId && !!connectionId,
  })
}

export function useXeroSyncJobRunsQuery(
  orgId?: string,
  connectionId?: string,
  jobId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<XeroSyncJobRunPayload[], Error>({
    queryKey: organizationKeys.xeroSyncJobRuns(orgId, connectionId, jobId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      return lighthouseService.listXeroSyncJobRuns(orgId, connectionId, jobId)
    },
    enabled: options?.enabled !== false && !!orgId && !!connectionId && !!jobId,
  })
}

export function useRetryXeroConnectionSyncMutation(orgId?: string, connectionId?: string, jobId?: string) {
  const queryClient = useQueryClient()
  return useMutation<XeroConnectionSyncRequest, Error>({
    mutationFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      return lighthouseService.retryXeroConnectionSync(orgId, connectionId, jobId)
    },
    onSuccess: () => {
      if (orgId && connectionId && jobId) {
        queryClient.invalidateQueries({ queryKey: organizationKeys.xeroSyncJobRuns(orgId, connectionId, jobId) })
      }
    },
  })
}

export function useXeroSyncJobRunQuery(
  orgId?: string,
  connectionId?: string,
  jobId?: string,
  runId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<XeroSyncJobRunDetailPayload, Error>({
    queryKey: organizationKeys.xeroSyncJobRun(orgId, connectionId, jobId, runId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!runId) throw new Error('Run ID is required')
      return lighthouseService.getXeroSyncJobRun(orgId, connectionId, jobId, runId)
    },
    enabled: options?.enabled !== false && !!orgId && !!connectionId && !!jobId && !!runId,
  })
}

export function useXeroSyncJobRunStepsQuery(
  orgId?: string,
  connectionId?: string,
  jobId?: string,
  runId?: string,
  stepType?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<XeroSyncJobRunStepPayload[], Error>({
    queryKey: organizationKeys.xeroSyncJobRunSteps(orgId, connectionId, jobId, runId, stepType),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!runId) throw new Error('Run ID is required')
      if (!stepType) throw new Error('Step type is required')
      return lighthouseService.listXeroSyncJobRunSteps(orgId, connectionId, jobId, runId, stepType)
    },
    enabled: options?.enabled !== false && !!orgId && !!connectionId && !!jobId && !!runId && !!stepType,
  })
}

/**
 * QuickBooks pipeline reads. Separate from the Xero hooks above.
 * There is no re-run hook.
 */
export function useQuickBooksSyncJobsQuery(orgId?: string, options?: { enabled?: boolean }) {
  return useQuery<QuickBooksSyncJobPayload[], Error>({
    queryKey: organizationKeys.quickbooksSyncJobs(orgId),
    queryFn: () => {
      if (!orgId) {
        throw new Error('Organization ID is required')
      }

      return lighthouseService.listQuickBooksSyncJobs(orgId)
    },
    enabled: options?.enabled !== false && !!orgId,
  })
}

export function useQuickBooksSyncJobRunsQuery(orgId?: string, jobId?: string, options?: { enabled?: boolean }) {
  return useQuery<Run<QuickBooksPipelineData>[], Error>({
    queryKey: organizationKeys.quickbooksSyncJobRuns(orgId, jobId),
    queryFn: () => {
      if (!orgId) {
        throw new Error('Organization ID is required')
      }

      if (!jobId) {
        throw new Error('Job ID is required')
      }

      return lighthouseService.listQuickBooksSyncJobRuns(orgId, jobId)
    },
    enabled: options?.enabled !== false && !!orgId && !!jobId,
  })
}

export function useQuickBooksSyncJobRunQuery(
  orgId?: string,
  jobId?: string,
  runId?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<Run<QuickBooksPipelineData>, Error>({
    queryKey: organizationKeys.quickbooksSyncJobRun(orgId, jobId, runId),
    queryFn: () => {
      if (!orgId) {
        throw new Error('Organization ID is required')
      }
      if (!jobId) {
        throw new Error('Job ID is required')
      }
      if (!runId) {
        throw new Error('Run ID is required')
      }

      return lighthouseService.getQuickBooksSyncJobRun(orgId, jobId, runId)
    },
    enabled: options?.enabled !== false && !!orgId && !!jobId && !!runId,
  })
}

export function useQuickBooksSyncJobRunStepsQuery(
  orgId?: string,
  jobId?: string,
  runId?: string,
  stepType?: string,
  options?: { enabled?: boolean },
) {
  return useQuery<RunStep<string, Record<string, unknown>>[], Error>({
    queryKey: organizationKeys.quickbooksSyncJobRunSteps(orgId, jobId, runId, stepType),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!runId) throw new Error('Run ID is required')
      if (!stepType) throw new Error('Step type is required')

      return lighthouseService.listQuickBooksSyncJobRunSteps(orgId, jobId, runId, stepType)
    },
    enabled: options?.enabled !== false && !!orgId && !!jobId && !!runId && !!stepType,
  })
}

export function useXeroPipelineQuery(orgId?: string, connectionId?: string, jobId?: string) {
  return useQuery<XeroIntegrationPipelinePayload, Error>({
    queryKey: organizationKeys.xeroPipeline(orgId, connectionId, jobId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      return lighthouseService.getXeroPipeline(orgId, connectionId, jobId)
    },
    enabled: !!orgId && !!connectionId && !!jobId,
  })
}

/**
 * Fetches pipeline transactions for a job.
 */
export function useXeroPipelineTransactionsQuery(orgId?: string, connectionId?: string, jobId?: string) {
  return useQuery<XeroIntegrationPipelineTransactionPayload[], Error>({
    queryKey: organizationKeys.xeroPipelineTransactions(orgId, connectionId, jobId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      return lighthouseService.listXeroPipelineTransactions(orgId, connectionId, jobId)
    },
    enabled: !!orgId && !!connectionId && !!jobId,
  })
}

/**
 * Fetches sync logs for a specific transaction.
 */
export function useXeroPipelineTransactionLogsQuery(
  orgId?: string,
  connectionId?: string,
  jobId?: string,
  transactionId?: string,
  stage?: string,
) {
  return useQuery<XeroIntegrationPipelineStageLogPayload[], Error>({
    queryKey: organizationKeys.xeroPipelineTransactionLogs(orgId, connectionId, jobId, transactionId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!connectionId) throw new Error('Connection ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!transactionId) throw new Error('Transaction ID is required')
      if (!stage) throw new Error('Stage is required')
      return lighthouseService.getXeroPipelineTransactionLogs(orgId, connectionId, jobId, transactionId, stage)
    },
    enabled: !!orgId && !!connectionId && !!jobId && !!transactionId && !!stage,
  })
}

export function useReportGenerationJobsQuery(
  orgId?: string,
  options?: { enabled?: boolean; vesselId?: string; connectedAccountId?: string; statuses?: GeneratedReportStatus[] },
) {
  return useQuery<ReportGenerationJobPayload[], Error>({
    queryKey: [
      ...organizationKeys.reportGenJobs(orgId),
      options?.vesselId,
      options?.connectedAccountId,
      options?.statuses,
    ],
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      return lighthouseService.listReportGenerationJobs(orgId, {
        vesselId: options?.vesselId,
        connectedAccountId: options?.connectedAccountId,
        statuses: options?.statuses,
      })
    },
    enabled: options?.enabled !== false && !!orgId,
  })
}

export function useReportGenerationJobRunsQuery(
  orgId?: string,
  jobId?: string,
  options?: { enabled?: boolean; take?: number; skip?: number },
) {
  return useQuery<Run<ReportGenerationPipelineData>[], Error>({
    queryKey: [...organizationKeys.reportGenJobRuns(orgId, jobId), options?.take, options?.skip],
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!jobId) throw new Error('Job ID is required')
      return lighthouseService.listReportGenerationJobRuns(orgId, jobId, {
        take: options?.take,
        skip: options?.skip,
      })
    },
    enabled: options?.enabled !== false && !!orgId && !!jobId,
  })
}

export function useReportGenerationJobRunQuery(orgId?: string, jobId?: string, runId?: string) {
  return useQuery<Run<ReportGenerationPipelineData>, Error>({
    queryKey: organizationKeys.reportGenJobRun(orgId, jobId, runId),
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!runId) throw new Error('Run ID is required')
      return lighthouseService.getReportGenerationJobRun(orgId, jobId, runId)
    },
    enabled: !!orgId && !!jobId && !!runId,
  })
}

export function useReportGenerationRunStepsQuery(
  orgId?: string,
  jobId?: string,
  runId?: string,
  stepType?: string,
  options?: { enabled?: boolean; take?: number; skip?: number },
) {
  return useQuery<RunStep<string, Record<string, unknown>>[], Error>({
    queryKey: [...organizationKeys.reportGenRunSteps(orgId, jobId, runId, stepType), options?.take, options?.skip],
    queryFn: () => {
      if (!orgId) throw new Error('Organization ID is required')
      if (!jobId) throw new Error('Job ID is required')
      if (!runId) throw new Error('Run ID is required')
      if (!stepType) throw new Error('Step type is required')
      return lighthouseService.listReportGenerationRunSteps(orgId, jobId, runId, stepType, {
        take: options?.take,
        skip: options?.skip,
      })
    },
    enabled: options?.enabled !== false && !!orgId && !!jobId && !!runId && !!stepType,
  })
}

export type CreateVesselMutationPayload = {
  name: string
  type?: AwxConnectedAccountType
  entityId?: string
}

type ListVesselsParams = {
  organizationId?: string
  take?: number
  skip?: number
  enabled?: boolean
}

/**
 * TanStack Query hook for listing organization vessels.
 * When organizationId is provided (e.g. from URL), uses it; otherwise uses activeOrganizationId from context.
 * Include organizationId in the call when rendering a specific org's vessels page so the correct data is shown.
 */
export function useListOrganizationVesselsQuery(params?: ListVesselsParams) {
  const { activeOrganizationId } = useUser()
  const { organizationId: paramOrgId, enabled = true, take = 100, skip = 0 } = params || {}

  const organizationId = paramOrgId ?? activeOrganizationId
  const isEnabled = enabled && Boolean(organizationId)

  return useQuery<{ vessels: VesselConnectedAccountAccess[] }>({
    queryKey: ['organization-vessels', organizationId, { take, skip }],
    queryFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.listOrganizationVessels(organizationId, { take, skip })
    },
    enabled: isEnabled,
  })
}

type InfiniteVesselsParams = {
  organizationId?: string
  enabled?: boolean
}

/**
 * TanStack Infinite Query hook for fetching organization vessels with infinite scroll.
 *
 * @param params - Optional parameters including organizationId override and enabled flag
 * @param pageSize - Number of vessels to fetch per page (default: 20)
 * @returns Infinite query result with pages of vessels, loading state, and fetchNextPage function
 *
 * @example
 * ```ts
 * const {
 *   data,
 *   fetchNextPage,
 *   hasNextPage,
 *   isFetchingNextPage
 * } = useInfiniteOrganizationVesselsQuery({}, 20)
 *
 * // Access all vessels across pages
 * const allVessels = data?.pages.flatMap((page) => page.vessels) ?? []
 * ```
 */
export function useInfiniteOrganizationVesselsQuery(params?: InfiniteVesselsParams, pageSize = 20) {
  const { activeOrganizationId } = useUser()
  const { organizationId: paramOrgId, enabled = true } = params || {}

  const organizationId = paramOrgId ?? activeOrganizationId
  const isEnabled = enabled && Boolean(organizationId)

  return useInfiniteQuery<{ vessels: VesselConnectedAccountAccess[] }, Error>({
    queryKey: ['organization-vessels', organizationId, 'infinite', pageSize],
    queryFn: ({ pageParam = 0 }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.listOrganizationVessels(organizationId, {
        skip: pageParam as number,
        take: pageSize,
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.vessels.length < pageSize) {
        return undefined
      }
      return allPages.length * pageSize
    },
    initialPageParam: 0,
    enabled: isEnabled,
  })
}

type VesselAndConnectedAccountsParams = {
  vesselId: string | null
  enabled?: boolean
}

/**
 * TanStack Query hook for fetching a vessel and its connected accounts.
 * Uses activeOrganizationId from context; vesselId is passed as param.
 */
export function useVesselAndConnectedAccountsQuery(params: VesselAndConnectedAccountsParams) {
  const { activeOrganizationId } = useUser()
  const { vesselId, enabled = true } = params

  const isEnabled = enabled && Boolean(activeOrganizationId) && Boolean(vesselId)

  return useQuery<VesselAndConnectedAccounts | null>({
    queryKey: ['vessel-connected-accounts', activeOrganizationId, vesselId],
    queryFn: async () => {
      if (!activeOrganizationId) {
        throw new Error('Organization ID is required')
      }
      if (!vesselId) {
        throw new Error('Vessel ID is required')
      }
      const { vesselAndConnectedAccounts } = await lighthouseService.getVesselAndConnectedAccounts(
        activeOrganizationId,
        vesselId,
      )
      return vesselAndConnectedAccounts
    },
    enabled: isEnabled,
  })
}

/**
 * TanStack Query mutation hook for creating a vessel.
 * Uses activeOrganizationId from context.
 * Invalidates organization-vessels queries on success.
 *
 * @example
 * ```ts
 * const createVessel = useCreateVesselMutation()
 * const vessel = await createVessel.mutateAsync({ name: 'My Vessel', type: AwxConnectedAccountType.OPERATIONS })
 * ```
 */
export function useCreateVesselMutation() {
  const queryClient = useQueryClient()
  const { activeOrganizationId } = useUser()

  return useMutation({
    mutationFn: async (payload: CreateVesselMutationPayload) => {
      if (!activeOrganizationId) {
        throw new Error('Organization ID is required')
      }
      return lighthouseService.createVessel(activeOrganizationId, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-vessels', activeOrganizationId],
      })
    },
  })
}

export function useConnectedAccountCardsQuery(
  orgId: string,
  vesselId: string,
  connectedAccountId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<CardPayload[]>({
    queryKey: ['connected-account-cards', orgId, vesselId, connectedAccountId],
    queryFn: async () => {
      return lighthouseService.getConnectedAccountCards(orgId, vesselId, connectedAccountId)
    },
    enabled: options?.enabled !== false,
  })
}

/**
 * Reconcile Airwallex cards (last 90 days) against the AnkorPay database. Disabled by default —
 * support triggers it explicitly via refetch(), since it may page through multiple Airwallex requests.
 */
export function useReconcileConnectedAccountCardsQuery(orgId: string, vesselId: string, connectedAccountId: string) {
  return useQuery<ReconciledCardPayload[]>({
    queryKey: ['connected-account-cards-reconcile', orgId, vesselId, connectedAccountId],
    queryFn: async () => {
      return lighthouseService.reconcileConnectedAccountCards(orgId, vesselId, connectedAccountId)
    },
    enabled: false,
    retry: false,
  })
}

export function useConnectedAccountCardCheckQuery(
  orgId: string,
  vesselId: string,
  connectedAccountId: string,
  cardId: string,
  options?: { enabled?: boolean },
) {
  return useQuery<[CardPayload | null, AWXCardResponse]>({
    queryKey: ['connected-account-card-check', orgId, vesselId, connectedAccountId, cardId],
    queryFn: async () => {
      return lighthouseService.getConnectedAccountCardCheck(orgId, vesselId, connectedAccountId, cardId)
    },
    enabled: options?.enabled !== false,
    retry: false,
  })
}

export function useBackfillConnectedAccountCardMutation() {
  const queryClient = useQueryClient()

  return useMutation<
    CardPayload,
    Error,
    { orgId: string; vesselId: string; connectedAccountId: string; cardId: string }
  >({
    mutationFn: async (args: { orgId: string; vesselId: string; connectedAccountId: string; cardId: string }) => {
      return lighthouseService.backfillConnectedAccountCard(
        args.orgId,
        args.vesselId,
        args.connectedAccountId,
        args.cardId,
      )
    },
    onSuccess: (_data, { orgId, vesselId, connectedAccountId, cardId }) => {
      // Re-run the check so the UI reflects that the card now exists in our database.
      queryClient.invalidateQueries({
        queryKey: ['connected-account-card-check', orgId, vesselId, connectedAccountId, cardId],
      })
      // Refresh the card list so the table picks up the newly backfilled card.
      queryClient.invalidateQueries({
        queryKey: ['connected-account-cards', orgId, vesselId, connectedAccountId],
      })
      // Note: the reconcile view (enabled: false) is refetched by its own component's onSuccess,
      // since invalidating a disabled query would not trigger a refetch anyway.
    },
  })
}

export function useReconcileConnectedAccountCardStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation<
    CardPayload,
    Error,
    { orgId: string; vesselId: string; connectedAccountId: string; cardId: string }
  >({
    mutationFn: async (args: { orgId: string; vesselId: string; connectedAccountId: string; cardId: string }) => {
      return lighthouseService.reconcileConnectedAccountCardStatus(
        args.orgId,
        args.vesselId,
        args.connectedAccountId,
        args.cardId,
      )
    },
    onSuccess: (_data, { orgId, vesselId, connectedAccountId }) => {
      // Refresh the card list so the table picks up the reconciled status.
      queryClient.invalidateQueries({
        queryKey: ['connected-account-cards', orgId, vesselId, connectedAccountId],
      })
      // Note: the reconcile view (enabled: false) is refetched by its own component's onSuccess,
      // since invalidating a disabled query would not trigger a refetch anyway.
    },
  })
}

/**
 * Reconcile the connected account's AnkorPay transfers against their webhook logs. Disabled by default,
 * support triggers it explicitly via refetch(), since it reduces one webhook log per transfer.
 */
export function useReconcileConnectedAccountTransfersQuery(
  orgId: string,
  vesselId: string,
  connectedAccountId: string,
) {
  return useQuery<ReconciledTransferPayload[]>({
    queryKey: ['connected-account-transfers-reconcile', orgId, vesselId, connectedAccountId],
    queryFn: async () => {
      return lighthouseService.reconcileConnectedAccountTransfers(orgId, vesselId, connectedAccountId)
    },
    enabled: false,
    retry: false,
  })
}

export function useReconcileConnectedAccountTransferStatusMutation() {
  return useMutation<
    ReconciledTransferPayload,
    Error,
    { orgId: string; vesselId: string; connectedAccountId: string; transferId: string }
  >({
    mutationFn: async (args) => {
      return lighthouseService.reconcileConnectedAccountTransferStatus(
        args.orgId,
        args.vesselId,
        args.connectedAccountId,
        args.transferId,
      )
    },
  })
}

export function useConnectedAccountGlobalAccountsQuery(orgId: string, vesselId: string, connectedAccountId: string) {
  return useQuery<LighthouseGlobalAccountPayload[]>({
    queryKey: ['connected-account-global-accounts', orgId, vesselId, connectedAccountId],
    queryFn: async () => {
      return lighthouseService.listConnectedAccountGlobalAccounts(orgId, vesselId, connectedAccountId)
    },
  })
}

/**
 * Generate the official Airwallex statement letter for a global account and download it in the browser.
 */
export function useDownloadConnectedAccountStatementMutation() {
  return useMutation<
    void,
    Error,
    { orgId: string; vesselId: string; connectedAccountId: string; globalAccountId: string }
  >({
    mutationFn: async ({ orgId, vesselId, connectedAccountId, globalAccountId }) => {
      const { blob, fileName } = await lighthouseService.generateConnectedAccountStatement(
        orgId,
        vesselId,
        connectedAccountId,
        globalAccountId,
      )
      const url = window.URL.createObjectURL(blob)
      try {
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } finally {
        window.URL.revokeObjectURL(url)
      }
    },
  })
}
